import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { Button, Card, Spin, Typography, Space, Alert } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { SIGN_IN_PAGE_PATH } from '../auth/SignInPage';

export const OAUTH_AUTHORIZE_PAGE_PATH = '/oauth/authorize';

const { Title, Paragraph, Text } = Typography;

/** Backend endpoint that issues the authorization code (outside /api/v1). */
const ISSUE_CODE_URL = `${import.meta.env.VITE_LEEDPULSE_API_URL}/api/external/v1/mcp/oauth/issue-code`;

/**
 * OAuth 2.1 consent page for the MCP server. The MCP client redirects the
 * browser here with the OAuth params; after the user (logged in via Firebase)
 * approves, we ask the backend for an authorization code and redirect back to
 * the client's redirect_uri.
 */
const OAuthAuthorizePage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const scope = params.get('scope') || '';

  const paramsValid = !!(clientId && redirectUri && codeChallenge);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => {
      setUser(u);
      setCheckingAuth(false);
      if (!u) {
        // Send to login, return here afterwards.
        const here = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `${SIGN_IN_PAGE_PATH}?redirect=${here}`;
      }
    });
    return () => unsub();
  }, []);

  /** Redirect back to the client with an error. */
  const denyRedirect = (err: string) => {
    const url = new URL(redirectUri);
    url.searchParams.set('error', err);
    if (state) url.searchParams.set('state', state);
    window.location.href = url.toString();
  };

  const onApprove = async () => {
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      const idToken = await user.getIdToken();
      const res = await fetch(ISSUE_CODE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ client_id: clientId, redirect_uri: redirectUri, code_challenge: codeChallenge, scope }),
      });
      const json = await res.json();
      const code = json?.data?.code;
      if (!res.ok || !code) {
        throw new Error(json?.data?.message || json?.message || 'Autorisation impossible.');
      }
      const url = new URL(redirectUri);
      url.searchParams.set('code', code);
      if (state) url.searchParams.set('state', state);
      window.location.href = url.toString();
    } catch (e: any) {
      setError(e?.message || 'Une erreur est survenue.');
      setSubmitting(false);
    }
  };

  const onDeny = () => denyRedirect('access_denied');

  if (checkingAuth || !user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!paramsValid) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24 }}>
        <Alert type="error" message="Requête d'autorisation invalide" description="Des paramètres OAuth sont manquants." showIcon />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 24 }}>
      <Card style={{ maxWidth: 460, width: '100%' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <img
              src="https://api.leedflow.com/api/v1/assets/leedflow/logo-leedflow.png"
              alt="Leedflow"
              style={{ height: 80 }}
            />
          </div>

          <Space align="center">
            <LockOutlined style={{ fontSize: 24 }} />
            <Title level={4} style={{ margin: 0 }}>Autoriser l'accès</Title>
          </Space>

          <Paragraph>
            Une application souhaite accéder <Text strong>en lecture seule</Text> à vos données Leedflow
            (contacts, appels, SMS, messages vocaux, tâches) via l'assistant IA.
          </Paragraph>

          <Alert
            type="info"
            showIcon
            message="Lecture seule"
            description="L'application pourra consulter vos données, mais ne pourra rien modifier, supprimer ou envoyer."
          />

          <Text type="secondary">Connecté en tant que {user.email}</Text>

          {error && <Alert type="error" message={error} showIcon />}

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onDeny} disabled={submitting}>Refuser</Button>
            <Button type="primary" onClick={onApprove} loading={submitting}>Autoriser</Button>
          </Space>
        </Space>
      </Card>
    </div>
  );
};

export default OAuthAuthorizePage;
